import {
  CAMPAIGN_DEFAULT_PLATFORM,
  CAMPAIGN_UNSUPPORTED_PLATFORM_MESSAGE,
  type CampaignCreatorItem,
  type CampaignDraftCreatorItem,
  type CampaignDraftPostItem,
  CampaignTrackingItemType,
  type CreatorSourceClassification,
  type ImportSummary,
  buildCampaignTrackPostItem,
  buildCreatorDedupKey,
  buildPostDedupKey,
  isCampaignSupportedPlatform,
  validateCreatorInput,
} from '@/entities/campaign';

export type CampaignCreateStep = 1 | 2 | 3;

export type CreatorCsvPreviewState = {
  rows: CampaignDraftCreatorItem[];
  summary: ImportSummary;
  suggestedCampaignName: string;
  limitSkippedCount: number;
  unsupportedPlatformCount: number;
  validationErrorCount: number;
};

export type PostCsvPreviewState = {
  rows: CampaignDraftPostItem[];
  summary: ImportSummary;
  suggestedCampaignName: string;
  limitSkippedCount: number;
  unsupportedPlatformCount: number;
  validationErrorCount: number;
};

export type SelectedCreatorListSummary = {
  id: string;
  title: string;
  importedCount: number;
};

export type CampaignCreateIssue = {
  rowId: string;
  message: string;
};

export const CAMPAIGN_CREATE_TABLE_PAGE_SIZE = 10;
const SUPPORTED_POST_URL_MESSAGE = 'Use a supported Instagram or TikTok post URL.';

function getCreatorPlatform(
  row: CampaignDraftCreatorItem,
  classification: CreatorSourceClassification,
) {
  return classification.detectedPlatform ?? row.platform;
}

function isUnsupportedPlatformMessage(message: string) {
  return message === CAMPAIGN_UNSUPPORTED_PLATFORM_MESSAGE;
}

export function getUnsupportedPlatformCount(summary: ImportSummary) {
  return summary.errors.filter((error) => isUnsupportedPlatformMessage(error.message)).length;
}

export function warnUnsupportedCampaignPlatforms(count: number) {
  return count > 0 ? CAMPAIGN_UNSUPPORTED_PLATFORM_MESSAGE : undefined;
}

export const emptyPostItem = (): CampaignDraftPostItem => ({
  id: crypto.randomUUID(),
  postUrl: '',
});

export const emptyCreatorItem = (): CampaignDraftCreatorItem => ({
  id: crypto.randomUUID(),
  sourceValue: '',
  platform: '',
});

export function withFallbackCreatorRow(rows: CampaignDraftCreatorItem[]) {
  return rows.length > 0 ? rows : [emptyCreatorItem()];
}

export function withFallbackPostRow(rows: CampaignDraftPostItem[]) {
  return rows.length > 0 ? rows : [emptyPostItem()];
}

function titleCaseWords(value: string) {
  return value
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function buildCampaignNameSuggestion(value: string) {
  const normalizedValue = value
    .replace(/\.[^/.]+$/, '')
    .replace(/[-_]+/g, ' ')
    .trim();
  if (!normalizedValue) return '';
  return titleCaseWords(normalizedValue);
}

export function getCreatorRowError(row: CampaignDraftCreatorItem) {
  const sourceValue = row.sourceValue.trim();
  if (!sourceValue) return undefined;

  const { classification, error } = validateCreatorInput(sourceValue);
  if (!classification) {
    return error;
  }

  if (!classification.detectedPlatform && !row.platform) {
    return 'Choose a platform for this handle.';
  }

  if (!isCampaignSupportedPlatform(getCreatorPlatform(row, classification))) {
    return CAMPAIGN_UNSUPPORTED_PLATFORM_MESSAGE;
  }

  return undefined;
}

export function getPostRowError(row: CampaignDraftPostItem) {
  const normalizedValue = row.postUrl.trim();
  if (!normalizedValue) return undefined;

  const postItem = buildCampaignTrackPostItem(normalizedValue);
  if (!postItem) {
    return SUPPORTED_POST_URL_MESSAGE;
  }

  if (!isCampaignSupportedPlatform(postItem.platform)) {
    return CAMPAIGN_UNSUPPORTED_PLATFORM_MESSAGE;
  }

  return undefined;
}

export function buildCreatorPayloadItem(
  row: CampaignDraftCreatorItem,
  classification: CreatorSourceClassification,
): CampaignCreatorItem | undefined {
  const platform = getCreatorPlatform(row, classification);
  if (!isCampaignSupportedPlatform(platform)) return undefined;

  return {
    type: CampaignTrackingItemType.HANDLE,
    handle: classification.normalizedHandle,
    platform,
    label: row.label,
  };
}

function splitBulkPasteLines(value: string) {
  return value
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export function buildCreatorBulkPastePreview(args: {
  text: string;
  existingRows: CampaignDraftCreatorItem[];
  availableSlots: number;
  itemLimit: number;
}): CreatorCsvPreviewState {
  const sourceValues = splitBulkPasteLines(args.text);
  const errors: ImportSummary['errors'] = [];
  const nextRows = [...args.existingRows];
  const seenKeys = new Set(nextRows.map((item) => buildCreatorDedupKey(item)).filter(Boolean));
  let added = 0;
  let duplicates = 0;
  let blockedByLimit = 0;

  sourceValues.forEach((sourceValue, index) => {
    const rowNumber = index + 1;
    const { classification, error } = validateCreatorInput(sourceValue);

    const nextItem: CampaignDraftCreatorItem = {
      id: crypto.randomUUID(),
      sourceValue: classification?.normalizedValue ?? sourceValue,
      platform: classification?.detectedPlatform ?? CAMPAIGN_DEFAULT_PLATFORM,
    };
    const sourceKey = classification ? buildCreatorDedupKey(nextItem) : undefined;

    if (!classification || !sourceKey) {
      errors.push({
        row: rowNumber,
        message: error ?? 'Use a creator handle or a supported profile URL.',
      });
      return;
    }

    if (!isCampaignSupportedPlatform(nextItem.platform)) {
      errors.push({
        row: rowNumber,
        message: CAMPAIGN_UNSUPPORTED_PLATFORM_MESSAGE,
      });
      return;
    }

    if (seenKeys.has(sourceKey)) {
      duplicates += 1;
      return;
    }

    if (added >= args.availableSlots) {
      blockedByLimit += 1;
      errors.push({
        row: rowNumber,
        message: `This row exceeds the ${args.itemLimit} item campaign limit.`,
      });
      return;
    }

    seenKeys.add(sourceKey);
    nextRows.push(nextItem);
    added += 1;
  });

  return {
    rows: nextRows,
    summary: {
      kind: 'creator',
      fileName: 'Bulk paste',
      added,
      duplicates,
      errors,
    },
    suggestedCampaignName: '',
    limitSkippedCount: blockedByLimit,
    unsupportedPlatformCount: errors.filter((error) => isUnsupportedPlatformMessage(error.message))
      .length,
    validationErrorCount:
      errors.length -
      blockedByLimit -
      errors.filter((error) => isUnsupportedPlatformMessage(error.message)).length,
  };
}

export function buildPostBulkPastePreview(args: {
  text: string;
  existingRows: CampaignDraftPostItem[];
  availableSlots: number;
  itemLimit: number;
}): PostCsvPreviewState {
  const postUrls = splitBulkPasteLines(args.text);
  const errors: ImportSummary['errors'] = [];
  const nextRows = [...args.existingRows];
  const seenKeys = new Set(
    args.existingRows.map((item) => buildPostDedupKey(item)).filter(Boolean),
  );
  let added = 0;
  let duplicates = 0;
  let blockedByLimit = 0;

  postUrls.forEach((postUrl, index) => {
    const rowNumber = index + 1;
    const postPayloadItem = buildCampaignTrackPostItem(postUrl);
    if (!postPayloadItem) {
      errors.push({
        row: rowNumber,
        message: SUPPORTED_POST_URL_MESSAGE,
      });
      return;
    }

    if (!isCampaignSupportedPlatform(postPayloadItem.platform)) {
      errors.push({
        row: rowNumber,
        message: CAMPAIGN_UNSUPPORTED_PLATFORM_MESSAGE,
      });
      return;
    }

    const nextItem: CampaignDraftPostItem = {
      id: crypto.randomUUID(),
      postUrl: postPayloadItem.post_url,
    };

    const dedupKey = buildPostDedupKey(nextItem);
    if (!dedupKey) {
      errors.push({
        row: rowNumber,
        message: SUPPORTED_POST_URL_MESSAGE,
      });
      return;
    }

    if (seenKeys.has(dedupKey)) {
      duplicates += 1;
      return;
    }

    if (added >= args.availableSlots) {
      blockedByLimit += 1;
      errors.push({
        row: rowNumber,
        message: `This row exceeds the ${args.itemLimit} item campaign limit.`,
      });
      return;
    }

    seenKeys.add(dedupKey);
    nextRows.push(nextItem);
    added += 1;
  });

  return {
    rows: nextRows,
    summary: {
      kind: 'post',
      fileName: 'Bulk paste',
      added,
      duplicates,
      errors,
    },
    suggestedCampaignName: '',
    limitSkippedCount: blockedByLimit,
    unsupportedPlatformCount: errors.filter((error) => isUnsupportedPlatformMessage(error.message))
      .length,
    validationErrorCount:
      errors.length -
      blockedByLimit -
      errors.filter((error) => isUnsupportedPlatformMessage(error.message)).length,
  };
}

export function focusElement(target: HTMLElement | null | undefined) {
  target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  target?.focus();
}
